import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, Pause, X } from "lucide-react";
import { useAudio } from "../context/AudioContext";

function format(t) {
  if (!isFinite(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function AudioPlayerBar() {
  const { track, playing, toggle, close, audioRef, setPlaying } = useAudio();
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setProgress(0);
    setDuration(0);
  }, [track?.url]);

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    a.currentTime = ratio * duration;
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={track?.url}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />
      <AnimatePresence>
        {track && (
          <motion.div
            data-testid="audio-player-bar"
            className="fixed bottom-0 inset-x-0 z-50 bg-jm-coal/95 backdrop-blur-xl border-t border-jm-brass/25"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              data-testid="audio-progress-track"
              className="h-[3px] bg-jm-surface cursor-pointer group"
              onClick={seek}
            >
              <div
                className="h-full bg-jm-amber transition-[width] duration-150"
                style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
              />
            </div>
            <div className="jm-container flex items-center gap-4 py-3">
              <button
                data-testid="audio-play-pause-button"
                onClick={toggle}
                className="w-10 h-10 shrink-0 rounded-full border border-jm-amber/60 text-jm-amber flex items-center justify-center hover:bg-jm-amber hover:text-jm-ink transition-colors duration-300"
                aria-label={playing ? "Pause" : "Lecture"}
              >
                {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
              </button>
              <div className="flex items-end gap-[3px] h-5 shrink-0" aria-hidden="true">
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className={`w-[3px] bg-jm-brass rounded-sm ${playing ? "jm-eq-bar" : "scale-y-[0.25] origin-bottom"}`}
                    style={{ height: "100%", animationDelay: `${i * 0.14}s` }}
                  />
                ))}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-jm-bone truncate">{track.title}</p>
                <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-jm-mute truncate">
                  {track.artist} — extrait de démonstration
                </p>
              </div>
              <span className="hidden sm:block font-mono text-xs text-jm-mute shrink-0">
                {format(progress)} / {format(duration)}
              </span>
              <button
                data-testid="audio-close-button"
                onClick={close}
                className="p-2 text-jm-mute hover:text-jm-bone transition-colors shrink-0"
                aria-label="Fermer le lecteur"
              >
                <X size={17} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
